// Chakra imports
import { CopyIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Link,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import CookieConsent from 'components/CookieConsent';
import { DateTime } from 'luxon';
import { QRCodeCanvas } from 'qrcode.react';
import { useCallback, useEffect, useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from '@google-recaptcha/react';
import { AiOutlineCheckCircle } from 'react-icons/ai';
import { GrClose } from 'react-icons/gr';
import { MdBarChart, MdCreditCard } from 'react-icons/md';
import { getChargeFromPublic, registerBilletForCharge } from 'services/api.service';
import useAnalyticsEventTracker from 'useAnalyticsEventTracker';
import usePixel from 'usePixel';
import { maskCurrency } from 'utils/number';
import { v4 as uuidv4 } from 'uuid';
import { StatusRender } from 'views/access/charges';
import { getVariable } from 'whitelabel';
import EquisCard from './Equis';
import MercadoPago from './MercadoPago';
import PagSeguroCard from './PagSeguroCard';
import { ChargePublic, CustomerAddress } from './types/ChargeTypes';

declare global {
  interface Window {
    PagSeguro: any;
  }
}

const ChargeMethods: React.FC<{
  charge: ChargePublic;
  loadCharge: () => {};
  antiFraudId: string;
}> = ({ charge, loadCharge, antiFraudId }) => {
  const toast = useToast();
  const ga = useAnalyticsEventTracker(`charge/:id`);
  const [selectedMethod, setSelectedMethod] = useState<string>('pix');
  const [loadingGenBillet, setLoadingGenBillet] = useState(false);
  const [showMercadoPago, setShowMercadoPago] = useState(false);

  const { executeV3 } = useGoogleReCaptcha();

  // Create an event handler so you can call the verification on button click event or form submit
  const handleReCaptchaVerify = useCallback(
    async (type = 'REGISTER_MERCADOPAGO') => {
      if (!executeV3) {
        console.log('Execute recaptcha not yet available');
        return;
      }

      return await executeV3(type);
      // Do whatever you want with the token
    },
    [executeV3],
  );

  const billet = charge.Billet?.[0];

  const handleGenerateBillet = async () => {
    ga(`gerar boleto`);
    setLoadingGenBillet(true);
    const token = await handleReCaptchaVerify('REGISTER_BILLET');

    registerBilletForCharge(charge.id, token)
      .then(() => {
        setLoadingGenBillet(false);
        toast({
          title: 'Boleto gerado com sucesso',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        loadCharge();
      })
      .catch((err) => {
        ga(`gerar boleto`, `erro`);

        setLoadingGenBillet(false);
        toast({
          title: err?.response?.data?.message ?? 'Erro ao gerar boleto',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      });
  };

  const PaymentMethodCard: React.FC<{
    icon: any;
    title: string;
    onClick: () => void;
  }> = ({ icon, title, onClick }) => (
    <Box
      as="button"
      w="100%"
      p={4}
      borderWidth="1px"
      borderRadius="lg"
      borderColor="gray.200"
      bg="white"
      _hover={{
        bg: 'brand.50',
        borderColor: 'brand.400',
        transform: 'translateY(-2px)',
        boxShadow: 'md',
      }}
      transition="all 0.2s"
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
    >
      <Icon
        as={icon}
        boxSize={6}
        color="brand.400"
        mr={3}
      />
      <Text
        fontSize="md"
        fontWeight="semibold"
      >
        {title}
      </Text>
    </Box>
  );

  return (
    <VStack
      w="100%"
      spacing={4}
    >
      {/* PIX sempre visível */}
      {charge.features.pix && selectedMethod === 'pix' && (
        <Box
          w="100%"
          textAlign="center"
        >
          {charge.Pix[0]?.qrCode ? (
            <Flex
              flexDirection="column"
              alignItems="center"
            >
              <Text
                fontSize={30}
                mb="2"
              >
                Valor: <b>{maskCurrency(charge.amount)}</b>
              </Text>
              <Text
                fontWeight="bold"
                mb="2"
              >
                QRcode Pix para pagamento
              </Text>
              <Stack
                direction={{
                  base: 'column',
                  md: 'row',
                }}
                alignItems="center"
                spacing={4}
              >
                <Box
                  backgroundColor="whiteAlpha.600"
                  borderWidth="1px"
                  borderRadius="lg"
                  padding={5}
                  width="100%"
                  maxW="250px"
                >
                  <QRCodeCanvas
                    size={200}
                    value={charge.Pix[0].qrCode}
                  />
                </Box>
                <Text
                  justifyContent="flex-start"
                  textAlign="left"
                  fontSize="sm"
                >
                  <b> O que é o Pix?</b>
                  <br /> O Pix é a nova modalidade de transferências do banco central, que funcionam 24 horas por dia e
                  possuem confirmação em tempo real.
                  <br />
                  Procure em seu aplicativo de banco ou conta digital a funcionalidade e escaneie o QR Code ao lado ou
                  copie o código usando o Pix Copia e Cola para efetuar um pagamento.
                </Text>
              </Stack>
              <Button
                mt={6}
                mb="2"
                size="sm"
                colorScheme="brand"
                variant="outline"
                leftIcon={<Icon as={CopyIcon} />}
                onClick={() => {
                  navigator.clipboard.writeText(charge.Pix[0].qrCode);
                  toast({
                    title: 'QRCode copiado para área de transferência',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                  });
                }}
              >
                Pix Copia e Cola
              </Button>
            </Flex>
          ) : (
            <>
              <Text
                fontSize={30}
                mb="2"
              >
                Valor: <b>{maskCurrency(charge.amount)}</b>
              </Text>
              <Text
                fontWeight="bold"
                mt={6}
                mb="2"
              >
                QRcode Pix para pagamento
              </Text>
              <Text fontSize="sm">QRCode não gerado. Por favor, entre em contato com o vendedor.</Text>
            </>
          )}
        </Box>
      )}

      {/* Boleto */}
      {charge.features.billet && selectedMethod === 'billet' && (
        <Box
          w="100%"
          textAlign="center"
        >
          <Text
            fontSize={30}
            mb="2"
          >
            Valor: <b>{maskCurrency(charge.amount)}</b>
          </Text>
          <Text
            fontWeight="bold"
            mt={6}
            mb="2"
          >
            Boleto para pagamento.
          </Text>
          {billet ? (
            <>
              <Button
                mb="2"
                size="sm"
                colorScheme="brand"
                onClick={() => {
                  ga(`copiar codigo de barras`);
                  navigator.clipboard.writeText(billet.billetDigit);
                  toast({
                    title: 'Boleto copiado para área de transferência',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                  });
                }}
              >
                Copiar código de barras
              </Button>
              <Link
                colorScheme="brand"
                href={charge.billetPdf}
                target="_blank"
              >
                Abrir boleto para pagamento
              </Link>
            </>
          ) : (
            <>
              <Text
                fontWeight="bold"
                mt={6}
                mb="2"
              >
                Boleto não gerado.
              </Text>
              <Button
                size="sm"
                colorScheme="brand"
                isLoading={loadingGenBillet}
                onClick={handleGenerateBillet}
              >
                Gerar Boleto
              </Button>
            </>
          )}
        </Box>
      )}

      {/* Equis Credit Card */}
      {charge.features.equis && selectedMethod === 'equis' && (
        <Box
          w="100%"
          textAlign="center"
        >
          <EquisCard
            method="CREDIT_CARD"
            id={charge.id}
            antiFraudId={antiFraudId}
            refetch={loadCharge}
            feeType={charge.feeType}
            amount={+charge.amount}
            installmentsConfig={charge.installmentsConfig}
          />
        </Box>
      )}

      {/* PagSeguro Credit Card */}
      {charge.features.pagSeguro && selectedMethod === 'pagseguro_credit' && (
        <Box
          w="100%"
          textAlign="center"
        >
          <PagSeguroCard
            method="CREDIT_CARD"
            id={charge.id}
            refetch={loadCharge}
            feeType={charge.feeType}
            amount={+charge.amount}
            installmentsConfig={charge.installmentsConfig}
          />
        </Box>
      )}

      {/* PagSeguro Debit Card */}
      {charge.features.pagSeguro && selectedMethod === 'pagseguro_debit' && (
        <Box
          w="100%"
          textAlign="center"
        >
          <PagSeguroCard
            method="DEBIT_CARD"
            refetch={loadCharge}
            feeType={charge.feeType}
            id={charge.id}
            amount={+charge.amount}
            installmentsConfig={charge.installmentsConfig}
          />
        </Box>
      )}

      {/* MercadoPago */}
      {charge.features.mercadoPago && selectedMethod === 'mercadopago' && (
        <Box
          w="100%"
          textAlign="center"
          maxW="450px"
          mx="auto"
        >
          {showMercadoPago && (
            <MercadoPago
              id={charge.id}
              amount={+charge.amount}
              installmentsConfig={charge.installmentsConfig}
              feeType={charge.feeType}
            />
          )}
        </Box>
      )}

      {/* Cards de outras formas de pagamento */}
      {selectedMethod === 'pix' && (
        <VStack
          w="100%"
          spacing={3}
          mt={6}
        >
          <Text
            fontSize="lg"
            fontWeight="semibold"
            mb={2}
          >
            Outras formas de pagamento
          </Text>

          {charge.features.billet && (
            <PaymentMethodCard
              icon={MdBarChart}
              title="Pagar com Boleto"
              onClick={() => setSelectedMethod('billet')}
            />
          )}

          {charge.features.equis && (
            <PaymentMethodCard
              icon={MdCreditCard}
              title="Pagar por Cartão de Crédito"
              onClick={() => setSelectedMethod('equis')}
            />
          )}

          {charge.features.pagSeguro && (
            <>
              <PaymentMethodCard
                icon={MdCreditCard}
                title="Pagar por Cartão de Crédito"
                onClick={() => setSelectedMethod('pagseguro_credit')}
              />
              <PaymentMethodCard
                icon={MdCreditCard}
                title="Pagar por Cartão de Débito"
                onClick={() => setSelectedMethod('pagseguro_debit')}
              />
            </>
          )}

          {charge.features.mercadoPago && (
            <PaymentMethodCard
              icon={MdCreditCard}
              title="Pagar por Cartão de Crédito"
              onClick={() => {
                setShowMercadoPago(true);
                setSelectedMethod('mercadopago');
              }}
            />
          )}
        </VStack>
      )}

      {/* Botão para voltar ao PIX */}
      {selectedMethod !== 'pix' && charge.features.pix && (
        <Button
          mt={4}
          size="sm"
          variant="ghost"
          colorScheme="brand"
          onClick={() => {
            setSelectedMethod('pix');
            setShowMercadoPago(false);
          }}
        >
          Voltar para Pix
        </Button>
      )}
    </VStack>
  );
};

const ItemText: React.FC<{
  label: string;
  value: string | React.ReactNode;
}> = ({ label, value }) => (
  <HStack
    w="100%"
    mb={2}
    justifyContent="space-between"
    alignItems="center"
    borderBottom="1px solid #eee"
    pb={2}
  >
    <Badge size="xs">
      <Text fontWeight={'thin'}>{label}</Text>
    </Badge>
    <Text textAlign="right">{value}</Text>
  </HStack>
);

// Custom Chakra theme
export default function Charge({ match: { params } }) {
  // states and functions
  const chargeId = params.id;
  const [charge, setBill] = useState<ChargePublic>(null);
  const [isError, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const pixel = usePixel();
  const [hasConsent, setHasConsent] = useState(false);
  const consented = (value: boolean) => {
    setHasConsent(value);
  };
  const [antiFraudId, setAntiFraudId] = useState<string>(uuidv4());

  useEffect(() => {
    document.title = getVariable('name') + ' - Cobrança';
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link['rel'] = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link['href'] = getVariable('favicon');
  }, []);

  const loadCharge = useCallback(async () => {
    if (['PAID', 'CANCELED'].includes(charge?.status)) {
      return;
    }
    try {
      const data = await getChargeFromPublic(chargeId);

      // Atualiza os dados se o status mudou OU se há diferenças nos dados do boleto
      const hasBilletChanges =
        (data?.Billet?.[0] && !charge?.Billet?.[0]) || data?.Billet?.[0]?.id !== charge?.Billet?.[0]?.id;

      if (data?.status !== charge?.status || hasBilletChanges) {
        setBill(data);
        setIsLoading(false);
      }
    } catch (err) {
      setError(err);
      setIsLoading(false);
    }
  }, [charge?.status, charge?.Billet, chargeId]);

  useEffect(() => {
    if (hasConsent && charge?.pixelId) {
      pixel.configure(charge.pixelId);
    }
  }, [hasConsent, charge?.pixelId, pixel]);

  useEffect(() => {
    loadCharge();
    const ref = window.document.getElementsByTagName('script')[0];
    const script = window.document.createElement('script');
    script.src = 'https://assets.pagseguro.com.br/checkout-sdk-js/rc/dist/browser/pagseguro.min.js';
    script.async = true;
    ref.parentNode.insertBefore(script, ref);

    const refEquis = window.document.getElementsByTagName('script')[1];
    const scriptEquis = window.document.createElement('script');
    scriptEquis.src = 'https://h.online-metrix.net/fp/tags.js?org_id=k8vif92e&session_id=mtvz_br' + antiFraudId;
    scriptEquis.async = true;
    refEquis.parentNode.insertBefore(scriptEquis, refEquis);

    setTimeout(() => {
      //refresh page
      window.location.reload();
    }, 1000 * 60 * 30);

    const interval = setInterval(() => {
      loadCharge();
    }, 5000);

    return () => clearInterval(interval);
  }, [loadCharge, antiFraudId]);

  const getAddress = (customerAddress: CustomerAddress) => {
    if (!customerAddress) {
      return 'Não informado';
    }
    return `${customerAddress.address}, ${customerAddress.number_address}  ${
      customerAddress.complement ? `, ${customerAddress.complement}` : ''
    } - ${customerAddress.neigherhood} - ${customerAddress.Cep.City.name} - ${customerAddress.Cep.state.name} - ${
      customerAddress.Cep.cep
    }`;
  };

  return (
    <GoogleReCaptchaProvider
      siteKey={process.env.REACT_APP_RECAPTCHA}
      type="v3"
    >
      <VStack
        justifyContent={'flex-start'}
        mt={5}
        p={2}
      >
        <Stack
          w={{
            base: '100%',
            md: '90%',
            lg: '80%',
            xl: '70%',
          }}
          justifyContent="space-between"
          direction={{
            base: 'column',
            md: 'row',
          }}
        >
          <HStack
            w="100%"
            justifyContent="space-between"
            display={{
              base: 'none',
              md: 'flex',
            }}
          >
            <Image
              src={getVariable('logo')}
              width="180px"
            />
            <VStack alignItems="flex-end">
              <Text
                fontSize={32}
                fontWeight="bold"
              >
                {charge?.chargeType === 'BROKER' ? 'VENDA' : 'FATURA'} <StatusRender status={charge?.status} />
              </Text>
              <HStack>
                <Text fontSize={'xs'}>DATA DE EMISSÃO: </Text>
                <Text fontSize={'md'}>
                  {charge?.createdAt
                    ? DateTime.fromISO(charge.createdAt).toFormat('dd/MM/yyyy HH:mm')
                    : 'Não informado'}
                </Text>{' '}
              </HStack>
            </VStack>
          </HStack>
          {/* Mobile */}
          <VStack
            w="100%"
            justifyContent="space-between"
            display={{
              base: 'flex',
              md: 'none',
            }}
          >
            <Image
              src={getVariable('logo')}
              width="180px"
            />
            <VStack alignItems="flex-end">
              <Text
                fontSize={32}
                fontWeight="bold"
              >
                {charge?.chargeType === 'BROKER' ? 'VENDA' : 'FATURA'} <StatusRender status={charge?.status} />
              </Text>
            </VStack>
            <HStack>
              <Text fontSize={'xs'}>DATA DE EMISSÃO: </Text>
              <Text fontSize={'md'}>
                {charge?.createdAt ? DateTime.fromISO(charge.createdAt).toFormat('dd/MM/yyyy HH:mm') : 'Não informado'}
              </Text>{' '}
            </HStack>
          </VStack>
        </Stack>
        {!isLoading && charge ? (
          <Stack
            backgroundColor="whiteAlpha.600"
            borderWidth="1px"
            borderRadius="lg"
            margin={{
              base: '2',
              md: '4',
              lg: '6',
              xl: '2',
            }}
            width={{
              base: '100%',
              md: '90%',
              lg: '80%',
              xl: '70%',
            }}
            alignItems="flex-start"
            flexDirection={{
              base: 'column',
              md: 'row',
            }}
          >
            <VStack
              p="4"
              alignItems="flex-start"
              width="100%"
            >
              {charge.CustomerClient?.name && (
                <>
                  <Text fontSize="lg">
                    <b>Dados do recebedor </b>
                  </Text>
                  <ItemText
                    label="Nome: "
                    value={charge.CustomerClient?.name}
                  />
                  <ItemText
                    label="CPF/CNPJ: "
                    value={charge.CustomerClient?.document_number}
                  />
                  <ItemText
                    label="Endereço: "
                    value={getAddress(charge.CustomerClient?.CustomerAddress[0])}
                  />
                  <Alert
                    status="info"
                    variant="left-accent"
                    mt={3}
                    mb={3}
                    p={3}
                    borderRadius="md"
                  >
                    <AlertDescription fontSize="sm">
                      DiviPay Payments mandatária p/ recebimento em favor de {charge.CustomerClient?.name} (
                      {charge.CustomerClient?.document_number?.toString().replace(/\D/g, '').length === 6
                        ? 'CPF'
                        : 'CNPJ'}{' '}
                      {charge.CustomerClient?.document_number}), ref. ({charge.description}), Circ Bacen 3.682/2013.
                    </AlertDescription>
                  </Alert>
                </>
              )}

              <Text fontSize="lg">
                <b>Informações da cobrança</b>
              </Text>

              <ItemText
                label="Identificador: "
                value={<Text fontSize="xs">#{charge.id}</Text>}
              />
              <ItemText
                label="Valor: "
                value={<b>{maskCurrency(charge.amount)} </b>}
              />
              <ItemText
                label="Data de vencimento: "
                value={
                  charge.expireAt ? DateTime.fromISO(charge.expireAt).toFormat('dd/MM/yyyy HH:mm') : 'Não informado'
                }
              />
              <ItemText
                label="Outras informações: "
                value={charge.description}
              />

              {charge.chargeType === 'BROKER' && (
                <Alert
                  status="success"
                  variant="left-accent"
                  flexDirection="column"
                  alignItems="flex-start"
                  p={4}
                  borderRadius="md"
                >
                  <AlertTitle
                    fontSize="lg"
                    fontWeight="bold"
                    mb={2}
                  >
                    Informações da intermediação
                  </AlertTitle>
                  <AlertDescription fontSize="md">
                    Esse pagamento é intermediado pela plataforma {getVariable('name')}, e o valor será liberado no
                    prazo acordado entre as partes. <br /> Caso aconteça algum problema, você poderá solicitar o
                    reembolso do valor e uma análise será feita para determinar o valor a ser restituído.
                    <br /> <b>Por favor, verifique as informações dessa cobrança antes de finalizar o pagamento.</b>
                  </AlertDescription>
                </Alert>
              )}
              {charge.broker?.releaseInDays && (
                <ItemText
                  label="Prazo para liberação: "
                  value={`${charge.broker?.releaseInDays} dias`}
                />
              )}

              <Text fontSize="lg">
                <b>Dados do pagador</b>
              </Text>
              <ItemText
                label="Nome: "
                value={charge.person?.name}
              />
              {charge.person?.email !== 'not_found' && (
                <ItemText
                  label="Email: "
                  value={charge.person?.email}
                />
              )}

              {charge.person?.document && (
                <ItemText
                  label="CPF/CNPJ: "
                  value={charge.person?.document}
                />
              )}

              {charge.shipping && (
                <ItemText
                  label="Endereço: "
                  value={`
                ${charge.shipping?.street}, ${charge.shipping?.number}  ${
                    charge.shipping?.complement ? `, ${charge.shipping?.complement}` : ''
                  } - ${charge.shipping?.district} - ${charge.shipping?.city} - ${charge.shipping?.state} - ${
                    charge.shipping?.zipCode
                  }
                  `}
                />
              )}

              {charge.itens?.length > 0 && (
                <Text fontSize="lg">
                  <b>Itens da cobrança</b>
                </Text>
              )}
              <TableContainer width="100%">
                <Table
                  size="sm"
                  variant="striped"
                  colorScheme="brand"
                >
                  <Thead>
                    <Tr>
                      <Th>Descrição</Th>
                      <Th>Qtd</Th>
                      <Th isNumeric>Valor final</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {charge.itens.map((item, index) => (
                      <Tr key={index}>
                        <Td
                          maxW={200}
                          overflow={'hidden'}
                          textOverflow={'ellipsis'}
                          whiteSpace={'nowrap'}
                        >
                          {item.name}
                        </Td>
                        <Td>{item.quantity}</Td>
                        <Td isNumeric>{maskCurrency(item.amount)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                  <Tfoot>
                    <Tr>
                      <Th>Total </Th>
                      <Th></Th>
                      <Th isNumeric>
                        {maskCurrency(
                          charge.itens.length === 0
                            ? charge.amount
                            : charge.itens.reduce((acc, item) => acc + +item.amount, 0),
                        )}
                      </Th>
                    </Tr>
                    <Tr>
                      <Th>Desconto</Th>
                      <Th></Th>
                      <Th isNumeric>
                        {maskCurrency(
                          charge.itens.length === 0
                            ? 0
                            : charge.itens.reduce((acc, item) => acc + +item.amount, 0) - +charge.amount,
                        )}
                      </Th>
                    </Tr>
                    <Tr>
                      <Th>Valor final</Th>
                      <Th></Th>
                      <Th isNumeric>{maskCurrency(charge.amount)}</Th>
                    </Tr>
                  </Tfoot>
                </Table>
              </TableContainer>
            </VStack>
            <VStack
              width="100%"
              height="100%"
            >
              <Flex
                key={charge.id}
                width="100%"
                height="100%"
                alignItems="center"
                flexDirection="column"
              >
                <>
                  {charge.status === 'CANCELED' ? (
                    <>
                      <Icon
                        fontSize={130}
                        color="red.400"
                        as={GrClose}
                      ></Icon>
                      <Text
                        fontWeight="bold"
                        fontSize={24}
                        mb="2"
                      >
                        Cancelado
                      </Text>
                    </>
                  ) : charge.status === 'PENDING' ? (
                    <ChargeMethods
                      antiFraudId={antiFraudId}
                      charge={charge}
                      loadCharge={loadCharge}
                    />
                  ) : (
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      width="100%"
                      direction="column"
                    >
                      <Icon
                        fontSize={130}
                        color="green.400"
                        as={AiOutlineCheckCircle}
                      ></Icon>
                      <Text
                        fontWeight="bold"
                        fontSize={24}
                        mb="2"
                      >
                        Pago
                      </Text>
                    </Stack>
                  )}
                </>
              </Flex>
            </VStack>
          </Stack>
        ) : isError ? (
          <Stack
            mt={10}
            width="500px"
          >
            <Text>Erro ao carregar cobrança</Text>
          </Stack>
        ) : (
          <Stack
            mt={10}
            width="500px"
          >
            <Skeleton height="20px" />
            <Skeleton height="20px" />
            <Skeleton height="20px" />
          </Stack>
        )}
        <Button
          mt="6"
          size="sm"
          colorScheme="brand"
          onClick={() => {
            loadCharge();
          }}
        >
          Atualizar
        </Button>
        <CookieConsent onChange={consented} />
      </VStack>
    </GoogleReCaptchaProvider>
  );
}
